import React, { useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Drawer, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useActionHook } from "hooks";
import { useAccountSelector, useDealActions } from "store";
import { IInvestor, IInvestorBankDetails, IInvestorSignature, InvestorWithBank, UpdateInvestorDto } from "store/types";

type UpdateInvestor = Pick<
  IInvestor,
  "name" | "email" | "phone_number" | "company" | "additional_information" | "group" | "description"
> & {
  signatures: IInvestorSignature[];
};

type UpdateBankDetails = Record<keyof Omit<IInvestorBankDetails, "id" | "investor_id">, string>;

type FlatUpdateInvestor = UpdateInvestor & UpdateBankDetails & { signatures: IInvestorSignature[] };

const bankDetailsInitial: UpdateBankDetails = {
  account_name: "",
  bank_name: "",
  bank_code: "",
  branch_number: "",
  account_number: "",
  swift: "",
  iban: ""
};

const investorInitial: UpdateInvestor = {
  name: "",
  email: "",
  phone_number: "",
  company: "",
  additional_information: "",
  description: null,
  group: "",
  signatures: []
};

interface UpdateInvestorProps {
  investor: InvestorWithBank | null;
  onClose: () => void;
}

export const UpdateInvestor: React.FC<UpdateInvestorProps> = ({ investor, onClose }) => {
  const { updateInvestorAction } = useDealActions();
  const { support_investor_groups } = useAccountSelector() || {};

  const { loading, action } = useActionHook(updateInvestorAction, {
    onSuccess: onClose
  });

  const state: FlatUpdateInvestor = useMemo(() => {
    // console.log(rest);
    const initialState: FlatUpdateInvestor = {
      ...investorInitial,
      ...bankDetailsInitial
    };

    if (!investor) {
      return initialState;
    }

    // Pick investor values
    if (investor.name) initialState.name = investor.name;
    if (investor.email) initialState.email = investor.email;
    if (investor.phone_number) initialState.phone_number = investor.phone_number;
    if (investor.company) initialState.company = investor.company;
    if (investor.additional_information) initialState.additional_information = investor.additional_information;
    if (investor.description) initialState.description = investor.description;
    if (investor.group) initialState.group = investor.group;

    // Handle signatures
    if (investor.extended?.signatures) {
      initialState.signatures = investor.extended.signatures;
    }

    const { bank_details } = investor;
    if (bank_details && bank_details.length) {
      // Pick bank details
      for (const key in bankDetailsInitial) {
        const value = bank_details[0][key as keyof UpdateBankDetails];
        if (value) {
          initialState[key as keyof UpdateBankDetails] = value;
        }
      }
    }

    return initialState;
  }, [investor]);

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: state,
    onSubmit: (values) => {
      if (!investor?.id) return;
      // const changedValues = onlyChangedValues(values, state);
      const dataBeforeSend: UpdateInvestorDto = { id: investor?.id, group: values.group || null };

      // Set investors values
      if (values.name) dataBeforeSend.name = values.name;
      if (values.email) dataBeforeSend.email = values.email;
      if (values.phone_number) dataBeforeSend.phone_number = values.phone_number;
      if (values.company) dataBeforeSend.company = values.company;
      if (values.additional_information) dataBeforeSend.additional_information = values.additional_information;
      if (values.description !== undefined) dataBeforeSend.description = values.description;

      // Handle signatures
      if (values.signatures !== undefined) {
        dataBeforeSend.extended = {
          signatures: values.signatures
        };
      }

      // Set Bank details
      Object.keys(bankDetailsInitial).forEach((key) => {
        const value = values[key as keyof UpdateBankDetails];

        if (!value) return;

        if (dataBeforeSend.bank_details) {
          dataBeforeSend.bank_details = {
            ...dataBeforeSend.bank_details,
            ...{ [key]: value }
          };
        } else {
          dataBeforeSend.bank_details = {
            [key as keyof UpdateBankDetails]: value
          };
        }
      });

      if (Object.keys(dataBeforeSend).length < 2) {
        onClose();
        return;
      }

      action(dataBeforeSend);
    },
    enableReinitialize: true
  });

  const addSignature = () => {
    if (values.signatures.length < 4) {
      setFieldValue("signatures", [...values.signatures, { name: "", role: "" }]);
    }
  };

  const removeSignature = (index: number) => {
    setFieldValue(
      "signatures",
      values.signatures.filter((_, i) => i !== index)
    );
  };

  const updateSignature = (index: number, field: keyof IInvestorSignature, value: string) => {
    const newSignatures = [...values.signatures];
    newSignatures[index] = { ...newSignatures[index], [field]: value };
    setFieldValue("signatures", newSignatures);
  };

  return (
    <Drawer
      onClose={onClose}
      sx={{
        ".MuiPaper-root": {
          borderRadius: "0 12px 12px 0"
        }
      }}
      open={!!investor}
    >
      <Stack
        component="form"
        onSubmit={handleSubmit}
        sx={{ width: "434px", height: "100%", overflow: "hidden", borderRadius: "0 12px 12px 0" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ minHeight: "50px", backgroundColor: "#edf1f9", color: "#0a3142", padding: "0 20px 0 30px" }}
        >
          <Typography sx={{ color: "inherit" }}>Edit Investor</Typography>
          <IconButton onClick={onClose} sx={{ color: "inherit" }}>
            <CloseIcon sx={{ color: "inherit" }} />
          </IconButton>
        </Stack>
        <Stack sx={{ padding: "30px", overflow: "auto", gap: "24px", flexGrow: 1 }}>
          <TextField size="small" label="Investor Name" name="name" onChange={handleChange} value={values.name} />
          {support_investor_groups && (
            <TextField size="small" label="Investor Group" name="group" onChange={handleChange} value={values.group} />
          )}
          <TextField size="small" label="Email Address" name="email" onChange={handleChange} value={values.email} />
          <TextField
            size="small"
            label="Phone Number"
            name="phone_number"
            onChange={handleChange}
            value={values.phone_number}
          />
          <TextField size="small" label="Company" name="company" onChange={handleChange} value={values.company} />
          <TextField
            size="small"
            label="Additional Information"
            name="additional_information"
            onChange={handleChange}
            value={values.additional_information}
          />
          <TextField
            size="small"
            label="Alias"
            name="description"
            onChange={handleChange}
            value={values.description || ""}
          />
          <Typography
            sx={{
              fontSize: "16px",
              color: "#5d7782",
              paddingBottom: "4px",
              borderBottom: "1px solid #dae0e2",
              display: "flex",
              alignItems: "center"
            }}
          >
            Bank Account details
          </Typography>
          <TextField
            size="small"
            label="Account Name"
            name="account_name"
            onChange={handleChange}
            value={values.account_name}
          />
          <Stack direction="row" gap="24px" justifyContent="stretch" alignItems="center">
            <TextField
              size="small"
              label="Bank Name"
              name="bank_name"
              onChange={handleChange}
              value={values.bank_name}
            />
            <TextField
              size="small"
              label="Bank Code"
              name="bank_code"
              onChange={handleChange}
              value={values.bank_code}
            />
          </Stack>
          <Stack direction="row" gap="24px" justifyContent="stretch" alignItems="center">
            <TextField
              size="small"
              label="Branch No."
              name="branch_number"
              onChange={handleChange}
              value={values.branch_number}
            />
            <TextField
              size="small"
              label="Account No."
              name="account_number"
              onChange={handleChange}
              value={values.account_number}
            />
          </Stack>
          <TextField size="small" label="SWIFT" name="swift" onChange={handleChange} value={values.swift} />
          <TextField size="small" label="IBAN" name="iban" onChange={handleChange} value={values.iban} />
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            sx={{
              fontSize: "16px",
              color: "#5d7782",
              paddingBottom: "4px",
              borderBottom: "1px solid #dae0e2"
            }}
          >
            <Typography sx={{ color: "inherit", fontSize: "inherit" }}>Signatures (max 4)</Typography>
            <IconButton onClick={addSignature} disabled={values.signatures.length >= 4} sx={{ padding: "4px" }}>
              <AddIcon sx={{ height: 18, width: 18 }} />
            </IconButton>
          </Stack>
          {values.signatures.map((signature, index) => (
            <Stack key={index} sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Stack direction="row" gap="24px" justifyContent="stretch" alignItems="center">
                <TextField
                  size="small"
                  label={`Signature ${index + 1} Name`}
                  value={signature.name}
                  onChange={(e) => updateSignature(index, "name", e.target.value)}
                  required
                />
                <TextField
                  size="small"
                  label="Role"
                  value={signature.role || ""}
                  onChange={(e) => updateSignature(index, "role", e.target.value)}
                />
                <IconButton onClick={() => removeSignature(index)} sx={{ alignSelf: "center", padding: "4px" }}>
                  <DeleteIcon sx={{ height: 18, width: 18, fill: "#0a3142" }} />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          gap="10px"
          sx={{ padding: "24px", borderTop: "1px solid #dae0e2" }}
        >
          <Button disabled={loading} onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button disabled={loading} type="submit" variant="contained">
            Save
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};
